import { useCallback, useEffect, useState } from 'react';
import { del, get, patch, post } from '../api';
import { fmtWhen, NB_LOC } from '../lib';
import { useFail, useToast } from '../ui';

interface Nb { id: string; location: string; active: boolean; note: string | null; updatedAt: string | null; updatedBy: string | null; uses: number; openWith: string | null }

const LOCS = Object.keys(NB_LOC);
const USE: Record<string, string> = {
  ti: 'Estoque das reservas e dos empréstimos da TI',
  'ms-a': 'Notebooks da sala; a TI também pode emprestar', 'ms-m': 'Notebooks da sala; a TI também pode emprestar',
  sm: 'Empréstimos em Secretaria > Notebooks', sme: 'Empréstimos em Secretaria > Notebooks',
};

/**
 * Cadastro de notebooks (TI e Administrador): todos os notebooks do colégio, de todos os locais.
 * Incluir, renomear, mudar de local, desativar (ex.: em suporte) com o motivo, reativar e excluir (se nunca emprestado).
 */
export default function NbAdmView() {
  const toast = useToast(), fail = useFail();
  const [list, setList] = useState<Nb[] | null>(null);
  const [editing, setEditing] = useState<Nb | null>(null);
  const [f, setF] = useState({ id: '', location: 'ti', note: '' });
  const [loc, setLoc] = useState('');
  const [q, setQ] = useState('');
  const [showOff, setShowOff] = useState(true);
  const [off, setOff] = useState<{ id: string; reason: string } | null>(null);
  const load = useCallback(() => { get<Nb[]>('notebooks/registry').then(setList).catch(fail); }, [fail]);
  useEffect(load, [load]);
  if (!list) return <section className="view"><div className="empty">Carregando…</div></section>;

  const qq = q.trim().toUpperCase();
  const shown = list.filter(n => (!loc || n.location === loc) && (showOff || n.active) && (!qq || n.id.includes(qq) || (n.note ?? '').toUpperCase().includes(qq)));
  const count = (l: string) => list.filter(n => n.location === l && n.active).length;
  const reset = () => { setEditing(null); setF({ id: '', location: loc || 'ti', note: '' }); };

  const save = async () => {
    const id = f.id.trim().toUpperCase();
    if (!id) { toast('Informe a identificação do notebook (a etiqueta, ex.: CCAL0022).'); return; }
    try {
      if (editing) {
        await patch(`notebooks/registry/${encodeURIComponent(editing.id)}`, { newId: id !== editing.id ? id : undefined, location: f.location, note: f.note.trim() });
        toast(`Notebook atualizado: ${id}.`);
      } else {
        await post('notebooks/registry', { id, location: f.location, note: f.note.trim() || undefined });
        toast(`Notebook cadastrado: ${id} (${NB_LOC[f.location]}).`);
      }
      reset(); load();
    } catch (err) { fail(err); }
  };
  const deactivate = async (n: Nb, reason: string) => {
    try {
      await patch(`notebooks/registry/${encodeURIComponent(n.id)}`, { active: false, note: reason.trim() || 'Desativado' });
      toast(`${n.id} desativado. Ele sai das listas de empréstimo e do estoque, e o histórico fica guardado.`); setOff(null); load();
    } catch (err) { fail(err); }
  };
  const reactivate = async (n: Nb) => {
    try { await patch(`notebooks/registry/${encodeURIComponent(n.id)}`, { active: true, note: '' }); toast(`${n.id} reativado.`); load(); }
    catch (err) { fail(err); }
  };
  const remove = async (n: Nb) => {
    try { await del(`notebooks/registry/${encodeURIComponent(n.id)}`); toast(`${n.id} excluído.`); load(); } catch (err) { fail(err); }
  };
  const fld = { border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', height: 34, background: 'var(--surface)' };

  return (
    <section className="view">
      <div className="vhead"><div><span className="eyebrow">Tecnologia</span><h2>Cadastro de notebooks</h2>
        <p>Todos os notebooks do colégio, de todos os locais. Cadastre os novos, corrija a identificação, mude de local e desative os que estão em suporte (com o motivo). Os da TI formam o estoque das reservas; os de cada Sala Microsoft definem a quantidade da sala; os das secretarias aparecem em Secretaria &gt; Notebooks.</p></div></div>
      <div className="tiles">
        {LOCS.map(l => <div className="tile" key={l}><b>{count(l)}</b><span>{NB_LOC[l]}</span></div>)}
        <div className="tile"><b>{list.filter(n => !n.active).length}</b><span>desativados (suporte, baixa…)</span></div>
      </div>
      <div className="kgrid">
        <div className="side">
          <section className="panel"><div className="panel-h"><h2>{editing ? 'Editar notebook' : 'Novo notebook'}</h2><span className="sub">{editing ? `${editing.id} · ${NB_LOC[editing.location]}` : 'Use a identificação que está na etiqueta do notebook'}</span></div>
            <div className="sp-form">
              <div className="field" style={{ flex: '1 1 160px' }}><label htmlFor="naId">Identificação <span className="req" aria-hidden="true">*</span></label>
                <input id="naId" value={f.id} maxLength={10} placeholder="Ex.: CCAL0022" onChange={e => setF({ ...f, id: e.target.value.toUpperCase() })} /></div>
              <div className="field" style={{ flex: '1 1 200px' }}><label htmlFor="naLoc">Local</label>
                <select id="naLoc" value={f.location} onChange={e => setF({ ...f, location: e.target.value })}>{LOCS.map(l => <option key={l} value={l}>{NB_LOC[l]}</option>)}</select></div>
              <div className="field" style={{ flexBasis: '100%' }}><label htmlFor="naNote">Observação (opcional)</label>
                <input id="naNote" value={f.note} maxLength={200} placeholder="Ex.: tela trocada em 10/2026" onChange={e => setF({ ...f, note: e.target.value })} /></div>
              <p className="hint" style={{ flexBasis: '100%', margin: 0 }}>{USE[f.location]}.{editing && editing.uses > 0 && f.id.trim().toUpperCase() !== editing.id ? ' O histórico de empréstimos passa para o nome novo.' : ''}</p>
              <button type="button" className="btn sm" onClick={save}>{editing ? 'Salvar alterações' : 'Cadastrar notebook'}</button>
              {editing && <button type="button" className="btn ghost sm" onClick={reset}>Cancelar</button>}
            </div></section>
        </div>
        <div className="side">
          <section className="panel"><div className="panel-h"><h2>Notebooks cadastrados</h2>
            <div className="row" style={{ marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <select aria-label="Filtrar por local" value={loc} onChange={e => setLoc(e.target.value)} style={{ ...fld, flex: '1 1 180px' }}>
                <option value="">Todos os locais</option>{LOCS.map(l => <option key={l} value={l}>{NB_LOC[l]}</option>)}</select>
              <input aria-label="Buscar notebook" value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar pela identificação" style={{ ...fld, flex: '1 1 160px' }} />
              <label className="check" style={{ flex: 'none' }}><input type="checkbox" checked={showOff} onChange={e => setShowOff(e.target.checked)} /> Mostrar desativados</label></div></div>
            <div className="klist">{shown.length ? shown.map(n => (
              <div className="krow compact" key={n.id} style={n.active ? undefined : { background: 'var(--surface-2)' }}>
                <span className="rm"><b>{n.id}</b><small>{NB_LOC[n.location]}{n.note ? ' · ' + n.note : ''}</small>
                  {n.updatedBy && <small>Alterado por {n.updatedBy}{n.updatedAt ? ' em ' + fmtWhen(n.updatedAt) : ''}</small>}
                  {off?.id === n.id && <span className="sp-form" style={{ padding: '6px 0 0' }}>
                    <span className="field" style={{ flex: '1 1 200px' }}><label htmlFor="naOff">Motivo</label>
                      <input id="naOff" value={off.reason} maxLength={200} placeholder="Ex.: em suporte · tela quebrada" onChange={e => setOff({ id: n.id, reason: e.target.value })} autoFocus /></span>
                    <button type="button" className="btn sm" onClick={() => deactivate(n, off.reason)}>Desativar</button>
                    <button type="button" className="btn ghost sm" onClick={() => setOff(null)}>Voltar</button></span>}</span>
                <span className="st">{!n.active ? <span className="pill neutral">Desativado</span> : n.openWith ? <span className="pill warn">Com {n.openWith}</span> : <span className="pill free">Ativo</span>}
                  {off?.id !== n.id && <span className="row" style={{ flex: 'none', gap: 4 }}>
                    <button className="link-btn acc" onClick={() => { setEditing(n); setF({ id: n.id, location: n.location, note: n.note ?? '' }); }}>Editar</button>
                    {n.active ? <button className="link-btn" onClick={() => setOff({ id: n.id, reason: 'Em suporte' })}>Desativar</button> : <button className="link-btn acc" onClick={() => reactivate(n)}>Reativar</button>}
                    {!n.uses && <button className="link-btn" onClick={() => remove(n)}>Excluir</button>}</span>}</span>
              </div>)) : <div className="empty" style={{ padding: 20 }}>Nenhum notebook encontrado.</div>}</div></section>
        </div>
      </div>
    </section>
  );
}
